import { useFetchDeleteEventOrganizer } from '@pages/event/api/useFetchDeleteOrg';
import { PencilSquareIcon, PlusIcon, TrashIcon } from '@shared/assets/icons';
import { ADD_ORGANIZER_ERROR_MESSAGE, useFetchAddOrganizer } from '@pages/event/api/useFetchAddOrganizer';
import {
  OrganizerChannel,
  getSendQrcodeToast,
  useSendAuthQrcodeToOrganizer,
} from '@pages/event/api/useSendAuthQrcodeToOrganizer';
import { getOrganizerFullName } from '@features/organizer/lib/getOrganizerFullName';
import { useJwtClaims } from '@features/auth';
import { useGetOrganizers } from '@pages/event/api/useGetOrganizers';
import { EditOrganizerModal } from '@features/edit-organizer-modal';
import { OrganizerCodeModal } from '@features/organizer-code-modal';
import { ModalAddOrganizer } from '@features/modal-organizer';
import { IOrganizer } from '@features/organizer/model/types';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import { useConfirm } from '@shared/hooks/useConfirm';
import { ModalTickets } from '@features/modal-sms';
import { Button, Checkbox } from '@shared/ui/kit';
import { useToast } from '@shared/hooks/useToast';
import styles from './organizers-tab.module.scss';
import { useParams } from 'react-router-dom';
import { concatArray } from '@shared/lib';
import { useState } from 'react';
import clsx from 'clsx';

interface OrganizersTabProps {
  eventId: number;
}

export const OrganizersTab = ({ eventId }: OrganizersTabProps) => {
  const [isAddOrganizerModalOpen, setIsAddOrganizerModalOpen] = useState(false);
  const [isDeleteOrganizerModalOpen, setIsDeleteOrganizerModalOpen] = useState(false);
  const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);
  const [isSendTicketsModalOpen, setIsSendTicketsModalOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [codeOrganizer, setCodeOrganizer] = useState<IOrganizer | undefined>();
  const [editingOrganizer, setEditingOrganizer] = useState<IOrganizer | undefined>();

  const [{ fetching: addOrganizerFetching }, addOrganizer] = useFetchAddOrganizer();
  const { fetching: smsFetchingAdmin, sendAuthQrcode } = useSendAuthQrcodeToOrganizer();
  const { organizersList, refetch } = useGetOrganizers(eventId);
  const [, deleteOrganizer] = useFetchDeleteEventOrganizer();

  const { openConfirmDialog } = useConfirm();
  const { showToast } = useToast();
  const { userId } = useJwtClaims();
  const { id } = useParams();

  const senderName = getOrganizerFullName(organizersList, userId);

  const isIndeterminate = organizersList?.some(({ id }) => selectedIds.has(id || 0));
  const isAllChecked = organizersList?.every(({ id }) => selectedIds.has(id || 0));

  const onCheckAllClick = () => {
    if (isIndeterminate) {
      setSelectedIds(new Set());
      return;
    }

    const newIds = new Set(organizersList?.map(({ id }) => id || 0));
    setSelectedIds(newIds);
  };

  const onCheckboxClick = (id?: number) => {
    if (!id) return;

    if (selectedIds.has(id)) {
      selectedIds.delete(id);
    } else {
      selectedIds.add(id);
    }

    const newIds = new Set(selectedIds);
    setSelectedIds(newIds);
  };

  const handleAddOrganizer = async ({ firstName, lastName, phone }: IOrganizer) => {
    if (!id) return;
    const input = { eventId, phone, firstName, lastName };
    const res = await addOrganizer({ input });

    if (res.error) {
      showToast(ADD_ORGANIZER_ERROR_MESSAGE, 'error');
      return;
    }

    refetch();
    setIsAddOrganizerModalOpen(false);
  };

  const handleDeleteOrganizer = async (organizer: IOrganizer) => {
    const isDelete = await openConfirmDialog(
      `Удалить организатора ${organizer.firstName} ${organizer.lastName} ?`,
      'Подтвердите удаление',
      'Удалить'
    );

    if (!id || !organizer.id || !isDelete) return;

    const res = await deleteOrganizer({ userId: organizer.id, eventId });

    if (res.data && res.data.deleteEventOrganizer.rowsAffected > 0) {
      refetch();
    } else {
      showToast('Произошла ошибка при удалении организатора', 'error');
    }
  };

  const handleEditOrganizer = (organizer: IOrganizer) => {
    setEditingOrganizer(organizer);
    setIsDeleteOrganizerModalOpen(true);
  };

  const handleCloseEditOrganizerModal = () => {
    setIsDeleteOrganizerModalOpen(false);
    setEditingOrganizer(undefined);
  };

  const handleGetCode = (organizer: IOrganizer) => {
    setCodeOrganizer(organizer);
    setIsCodeModalOpen(true);
  };

  const handleCloseCodeModal = () => {
    setIsCodeModalOpen(false);
    setCodeOrganizer(undefined);
  };

  const handleSendQrcode = async (message: string, channel: OrganizerChannel) => {
    const outcome = await sendAuthQrcode({ message, users: Array.from(selectedIds), eventId, channel });

    setIsSendTicketsModalOpen(false);
    showToast(...getSendQrcodeToast(outcome));
  };

  return (
    <div>
      <div className={styles.header}>
        Список организаторов
        <Button
          onClick={() => setIsSendTicketsModalOpen(true)}
          size="small"
          variant="secondary"
          isDisabled={!selectedIds.size}
        >
          Отправить QR-код ({selectedIds.size})
        </Button>
      </div>

      <UnstyledButton className={styles.addButton} onClick={() => setIsAddOrganizerModalOpen(true)}>
        <PlusIcon />
        Добавить организатора
      </UnstyledButton>

      {!organizersList?.length && (
        <span className={styles.organizersEmpty}>В событие еще не добавлены организаторы</span>
      )}

      {!!organizersList?.length && (
        <div className={styles.organizer}>
          <div className={clsx(styles.organizerName, !isAllChecked && isIndeterminate && styles.indeterminateCheckbox)}>
            <Checkbox onChange={onCheckAllClick} isChecked={isAllChecked} isIndeterminate={isIndeterminate} />
            Выбрать всех
          </div>
        </div>
      )}

      {organizersList?.map((organizer) => (
        <div className={styles.organizer}>
          <div className={styles.organizerName}>
            <Checkbox onChange={() => onCheckboxClick(organizer.id)} isChecked={selectedIds.has(organizer.id || 0)} />
            {concatArray([organizer.lastName, organizer.firstName])}
          </div>

          <div className={styles.actionsContainer}>
            <Button onClick={() => handleGetCode(organizer)} size="xxs" variant="primary">
              Получить код
            </Button>

            <div className={styles.actions}>
              <Button onClick={() => handleEditOrganizer(organizer)} size="xxs" variant="color">
                <PencilSquareIcon color="currentColor" />
              </Button>

              <Button onClick={() => handleDeleteOrganizer(organizer)} size="xxs" variant="color">
                <TrashIcon color="currentColor" />
              </Button>
            </div>
          </div>
        </div>
      ))}

      <ModalAddOrganizer
        active={isAddOrganizerModalOpen}
        closeHandle={() => setIsAddOrganizerModalOpen(false)}
        addOrganizer={handleAddOrganizer}
        fetching={addOrganizerFetching}
      />

      <EditOrganizerModal
        isOpen={isDeleteOrganizerModalOpen}
        onClose={handleCloseEditOrganizerModal}
        organizer={editingOrganizer}
      />

      <OrganizerCodeModal
        isOpen={isCodeModalOpen}
        onClose={handleCloseCodeModal}
        organizer={codeOrganizer}
        eventId={eventId}
      />

      <ModalTickets
        active={isSendTicketsModalOpen}
        closeHandle={() => setIsSendTicketsModalOpen(false)}
        isLoading={smsFetchingAdmin}
        sendSMS={handleSendQrcode}
        eventId={eventId}
        isAdmin
        senderName={senderName}
        usersSMS={{ users: Array.from(selectedIds), tickets: [], isAdmin: true }}
        hasOrganizerWithoutPhone={Array.from(selectedIds).some(
          (userId) => !organizersList?.find(({ id }) => id === userId)?.phone?.trim()
        )}
      />
    </div>
  );
};
